import React from 'react';
import { CalendarCheck, FileSignature, Inbox, PhoneCall } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useSalesTeamMetrics } from '../../../../services/teamMetrics';
import {
  Avatar,
  Callout,
  Card,
  Delta,
  FlipCard,
  FlipHint,
  GoalPill,
  HUES,
  HeroCard,
  Ring,
  SURFACE,
  Skeleton,
  StatTile,
  Th,
  personColors,
} from './premium';
import { MiniBars, TrendChart, fmtPctV, fmtWait, labelsFor } from './teamCharts';
import FunnelFilters from '../dashboard/FunnelFilters';
import CallsCard from './CallsCard';
import { BonusLadder, sourceName, fmtDays, fmtMinutes, fmtPct, periodNote, periodLabel, vsText, vsTitle } from './teamUi';

// Sales Team (Nic 2026-10-01, the Envoy look 2026-10-25): how the reps are
// doing against the Objectives PDF. Speed to lead (reached out within 2
// minutes, goal 90%), agreements signed, live hot seats to onboarding (goal
// 80%), follow-ups booked, days from account to signed (Software Changes
// #6, 2026-10-25) and quarterly bonus progress. Every tile flips to
// its chart over the period: a month by day, a quarter by month.

const TINT = {
  sky: 'bg-gradient-to-br from-sky-400 to-sky-500 text-white shadow-sm shadow-sky-500/25',
  indigo: 'bg-gradient-to-br from-indigo-400 to-indigo-500 text-white shadow-sm shadow-indigo-500/25',
  emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-sm shadow-emerald-500/25',
  orange: 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-sm shadow-orange-500/25',
};

const SalesTeamTab = ({ period, filter, onFilter, onWindow, jumped }) => {
  const { data, isLoading, isError } = useSalesTeamMetrics(period, filter);
  // The Source menu (Nic 2026-10-25), the Funnel's own, kept across tabs.
  // Bonus progress (and the Sales tab's time away) always count every source.
  const filtered = !!(filter?.source || filter?.partnerId);
  const srcName = sourceName(filter, data?.filterOptions);
  const sourceRow = onFilter ? (
    <FunnelFilters
      showExport={false}
      source={filter?.source || ''}
      partnerId={filter?.partnerId || ''}
      filterOptions={data?.filterOptions}
      onFilter={onFilter}
      onWindow={onWindow}
      jumped={jumped}
    />
  ) : null;
  const team = data?.team;
  const prev = data?.previous;
  const series = data?.series || [];
  const reps = data?.reps || [];
  const colors = personColors(reps);
  const p = data?.period;
  const vs = vsText(p);
  const vsT = vsTitle(p);
  // How the charts bucket this period (the server's unit): day, week or month.
  const unit = data?.period?.unit || 'month';
  const labels = labelsFor(unit);
  const by = `by ${unit}`;
  const targets = data?.targets || { speedPct: 90, hotSeatPct: 80 };
  const hotRecorded = team ? team.hotSeat.live + team.hotSeat.callback > 0 : false;

  if (isError) {
    return (
      <Card className="py-14 text-center">
        <span className="text-sm text-slate-400">The Sales Team numbers couldn&apos;t be loaded.</span>
      </Card>
    );
  }
  if (isLoading || !team) {
    return (
      <div className="space-y-4">
        {sourceRow}
        <Skeleton className="h-56" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[10.25rem]" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  // The rep stack for the signed tile: one bar segment per rep, plus any
  // agreement no rep owned. Direct signups count as agreements (Nic
  // 2026-11-02: the rep still sends the agreement and gets it signed); the
  // Direct column in By rep is the breakdown.
  const directSigned = team.directSigned || 0;
  const repFields = [
    ...reps.map((r) => ({
      key: `rep_${r.userId}`,
      name: r.name,
      color: colors[r.userId],
      get: (b) => b.byRep?.[r.userId] || 0,
    })),
    ...(data.unattributedSigned
      ? [{ key: 'rep_none', name: 'No rep', color: HUES.slate, get: (b) => b.byRep?.none || 0 }]
      : []),
  ];

  return (
    <div className="space-y-4">
      {sourceRow}
      {/* Hero: the period's scoreboard. Click flips to how it's counted. */}
      <FlipCard
        label="Sales Team scoreboard"
        resetKey={period}
        front={
          <HeroCard hint={<FlipHint />}>
            <div className="flex flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:text-left">
              <div className="lg:justify-self-start">
                <h2 className="mb-0 text-2xl font-bold text-white">Sales Team</h2>
                <div className="mt-1 text-sm leading-relaxed text-slate-500">
                  {periodLabel(period)}
                  {srcName ? ` · ${srcName}` : ''}
                  <br />
                  {periodNote(p)}
                </div>
              </div>
              <div className="flex items-start justify-center gap-8 lg:justify-self-center">
                <Ring
                  value={team.speedAvailable?.pct}
                  target={targets.speedPct}
                  label="Speed to lead"
                  sub="within 2 minutes"
                />
                <Ring
                  value={hotRecorded ? team.hotSeat.livePct : null}
                  target={targets.hotSeatPct}
                  label="Live hot seats"
                  sub={hotRecorded ? 'handed over live' : 'none yet'}
                />
              </div>
              <div className="lg:justify-self-end lg:pr-6 lg:text-right">
                <div className="text-5xl font-bold leading-none tabular-nums text-white xl:text-6xl">
                  {team.signed.toLocaleString('en-US')}
                </div>
                <div className="mt-2.5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                  agreements signed
                </div>
                <div className="mt-2 flex flex-col items-center gap-1 lg:items-end">
                  <Delta value={team.signed} prev={prev?.signed} vs={vs} title={vsT} dark />
                  <span className="text-sm tabular-nums text-slate-500">
                    {team.reached.toLocaleString('en-US')} reached · {team.leads.toLocaleString('en-US')} new leads
                  </span>
                  <span className="text-sm tabular-nums text-slate-500">
                    median {fmtDays(team.medianDaysToSign)} from account to signed
                  </span>
                  {directSigned ? (
                    <span className="text-sm tabular-nums text-teal-300/90">
                      {directSigned.toLocaleString('en-US')} direct signup{directSigned === 1 ? '' : 's'} included
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </HeroCard>
        }
        back={
          <HeroCard hint={<FlipHint close />}>
            <div className="flex h-full flex-col">
              <h2 className="mb-0 text-base font-bold text-white">How it&apos;s counted</h2>
              {/* Short and scannable (Nic 2026-10-26: it got busy and ran into the
                  bottom): one line per number, the comparison note once. Four items,
                  two rows (Nic 2026-11-01: a fifth, Direct signups, ran off the card at
                  laptop widths; the Agreements signed tile's hover says the same). */}
              <div className="mx-auto grid w-full max-w-3xl flex-1 content-center gap-x-10 gap-y-3 sm:grid-cols-2">
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Speed to lead</div>
                  <div className="text-[13px] leading-snug text-slate-400">Reached out within 2 minutes, counting only time the rep was available. Goal {targets.speedPct}%.</div>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Live hot seats</div>
                  <div className="text-[13px] leading-snug text-slate-400">Signed on a call and handed to onboarding live. Goal {targets.hotSeatPct}%, rising to 100%.</div>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Agreements</div>
                  <div className="text-[13px] leading-snug text-slate-400">Each client&apos;s first signature, credited to the rep who signed them.</div>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">Account to signed</div>
                  <div className="text-[13px] leading-snug text-slate-400">Median time from the client making their account to signing.</div>
                </div>
              </div>
              <p className="mb-0 mt-2 text-center text-[11.5px] text-slate-500">Changes are {vsT.toLowerCase()}.</p>
            </div>
          </HeroCard>
        }
      />

      {/* Tiles: each flips to its chart over the period. */}
      <div>
        <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
          {periodLabel(period)}
          <span className="ml-2 normal-case tracking-normal text-slate-300">Click a tile to see it {by}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile
            flipKey={period}
            icon={Inbox}
            tint={TINT.sky}
            accent="text-sky-600"
            label="New leads"
            value={team.leads}
            info="Leads that arrived and became workable in this period. Signups the app started itself (direct signups, pull-ins) don't count."
            foot={<Delta value={team.leads} prev={prev?.leads} vs={vs} title={vsT} />}
            backCaption={by}
            back={<MiniBars series={series} short={labels.short} long={labels.long} fields={[{ key: 'leads', name: 'New leads', color: HUES.sky, get: (b) => b.leads }]} />}
          />
          <StatTile
            flipKey={period}
            icon={PhoneCall}
            tint={TINT.indigo}
            accent="text-indigo-600"
            label="Reached"
            value={team.reached}
            info="Leads the team had a real conversation with for the first time in this period."
            foot={<Delta value={team.reached} prev={prev?.reached} vs={vs} title={vsT} />}
            backCaption={by}
            back={<MiniBars series={series} short={labels.short} long={labels.long} fields={[{ key: 'reached', name: 'Reached', color: HUES.indigo, get: (b) => b.reached }]} />}
          />
          <StatTile
            flipKey={period}
            icon={FileSignature}
            tint={TINT.emerald}
            accent="text-emerald-600"
            label="Agreements signed"
            value={team.signed}
            info={`Each client's first signature, credited to the rep who signed them, direct signups included (the rep still sends the agreement and gets it signed).${data.unattributedSigned ? ` ${data.unattributedSigned} had no rep.` : ''} Direct signups are not counted as leads anywhere else.`}
            foot={
              directSigned ? (
                <span>
                  <Delta value={team.signed} prev={prev?.signed} vs={vs} title={vsT} />
                  <span className="ml-1 text-teal-600 dark:text-teal-400">{directSigned} direct</span>
                </span>
              ) : (
                <Delta value={team.signed} prev={prev?.signed} vs={vs} title={vsT} />
              )
            }
            backCaption={`${by}, by rep`}
            back={<MiniBars series={series} short={labels.short} long={labels.long} fields={repFields} />}
          />
          <StatTile
            flipKey={period}
            icon={CalendarCheck}
            tint={TINT.orange}
            accent="text-orange-600"
            label="Follow-ups booked"
            value={team.meetings.booked}
            info="Meetings booked with leads who wanted time to review, by the day they were booked."
            foot={
              <span>
                <b className="font-semibold text-rose-500">{team.meetings.noShows}</b> no-shows ·{' '}
                <b className="font-semibold text-slate-600 dark:text-slate-300">{team.meetings.within48h}</b> within 48h
              </span>
            }
            backCaption={by}
            back={
              <MiniBars
                series={series} short={labels.short} long={labels.long}
                fields={[
                  { key: 'showed', name: 'Booked', color: HUES.orange, get: (b) => b.booked - b.noShows },
                  { key: 'noShows', name: 'No-shows', color: HUES.rose, get: (b) => b.noShows },
                ]}
              />
            }
          />
        </div>
      </div>

      {/* Speed to lead over the period, against the 90% goal. */}
      <Card
        eyebrow={`Speed to lead · ${by}`}
        title="Reached out within 2 minutes"
        info="Bars: the share of new leads reached out to within 2 minutes while the rep was available. Line: the average wait. Today's bar is lighter: it's still filling in."
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[13rem_1fr]">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 lg:grid-cols-1 lg:content-start">
            <Callout color={HUES.indigo} label="While available" value={fmtPct(team.speedAvailable?.pct)}>
              <Delta value={team.speedAvailable?.pct} prev={prev?.speedAvailable?.pct} kind="pts" vs={vs} title={vsT} />
            </Callout>
            <Callout color={HUES.orange} label="Average wait" value={fmtWait(team.speedAvailable?.avgMinutes)}>
              <Delta
                value={team.speedAvailable?.avgMinutes}
                prev={prev?.speedAvailable?.avgMinutes}
                kind="minutes"
                invert
                vs={vs}
                title={vsT}
              />
            </Callout>
            <Callout color={HUES.slate} label="Nothing taken out" value={fmtPct(team.speed.pct)}>
              <span title="Meetings and breaks count against the wait too">
                average {fmtWait(team.speed.avgMinutes)}
                {team.speed.untouched ? ` · ${team.speed.untouched} not reached out to` : ''}
                {team.speed.notDue ? ` · ${team.speed.notDue} not due yet` : ''}
              </span>
            </Callout>
          </div>
          <TrendChart
            series={series} short={labels.short} long={labels.long}
            leftDomain={[0, 100]}
            leftFormat={(v) => `${v}%`}
            rightFormat={(v) => `${v}m`}
            goal={{ value: targets.speedPct, label: `GOAL ${targets.speedPct}%` }}
            fields={[
              {
                key: 'speedPct',
                name: 'Within 2 minutes',
                color: HUES.indigo,
                get: (b) => (b.leads ? b.speedPct : null),
                format: fmtPctV,
                axisNote: 'left',
              },
              {
                key: 'avgMinutes',
                name: 'Average wait',
                color: HUES.orange,
                type: 'line',
                axis: 'right',
                get: (b) => b.avgMinutes,
                format: fmtWait,
                axisNote: 'right',
              },
            ]}
          />
        </div>
      </Card>

      {/* Hot seats over the period, against the 80% goal. */}
      <Card
        eyebrow={`Hot seats · ${by}`}
        title="Handed to onboarding live"
        info="Of the clients who signed on a call: green were handed to an Onboarding Specialist live, amber got a callback instead. The line is the live share. RingCentral decides live or not, never the rep."
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_13rem]">
          <TrendChart
            series={series} short={labels.short} long={labels.long}
            rightDomain={[0, 100]}
            rightFormat={(v) => `${v}%`}
            goal={{ value: targets.hotSeatPct, label: `GOAL ${targets.hotSeatPct}%`, axis: 'right' }}
            fields={[
              { key: 'hotLive', name: 'Live', color: HUES.emerald, stack: 'hot', get: (b) => b.hotLive },
              { key: 'hotCallback', name: 'Callback', color: HUES.amber, stack: 'hot', get: (b) => b.hotCallback },
              {
                key: 'livePct',
                name: 'Live share',
                color: HUES.violet,
                type: 'line',
                axis: 'right',
                get: (b) => b.livePct,
                format: fmtPctV,
                axisNote: 'right',
              },
            ]}
          />
          {/* Live, Callback and Not sent add up to "signed on a call" (Nic
              2026-10-26). No "Signed with no call": never a hot seat. */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-1 lg:content-start">
            <Callout color={HUES.violet} label="Live share" value={hotRecorded ? fmtPct(team.hotSeat.livePct) : '-'}>
              {team.hotSeat.onCall} signed on a call
            </Callout>
            <Callout color={HUES.emerald} label="Live" value={team.hotSeat.live} />
            <Callout color={HUES.amber} label="Callback" value={team.hotSeat.callback} />
            <Callout color={HUES.slate} label="Not sent" value={team.hotSeat.notSent ?? 0}>
              signed on a call, no transfer
            </Callout>
          </div>
        </div>
      </Card>

      {/* Calls (Nic 2026-10-25): its own card. */}
      <CallsCard calls={data?.calls} team="sales" period={period} />

      {/* Per rep, Time away included. */}
      <Card eyebrow={periodLabel(period)} title="By rep" info="Each rep gets credit for the leads they own and the clients they signed.">
        <div className="-mx-5 overflow-x-auto">
          {/* Tighter (Nic 2026-10-26): short headers, initials, sublines on one
              line, less padding, everything centered, so it fits without a
              sideways scroll. */}
          <table className="w-full min-w-[720px] text-sm">
            {/* Option B (Nic 2026-11-02): two stories, named. The leads that
                ARRIVED in the period read as a flow (arrived, reached, how
                fast); the clients who SIGNED in it are a second group with
                the lead/direct share as a bar. Percent pills everywhere,
                counts on hover, so a year of numbers never widens a cell. */}
            <thead>
              <tr>
                <th className={cn('sticky left-0 z-10', SURFACE)} />
                <th colSpan={2} className="px-2 pb-0 pt-1">
                  <span className="block border-b-2 border-violet-400 pb-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:border-violet-500/70 dark:text-slate-400">
                    New leads this period
                  </span>
                </th>
                <th className="w-4 p-0" />
                <th colSpan={3} className="px-2 pb-0 pt-1">
                  <span className="block border-b-2 border-teal-400 pb-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:border-teal-500/70 dark:text-slate-400">
                    Agreements this period
                  </span>
                </th>
                <th className="w-4 p-0" />
                <th colSpan={2} className="px-2 pb-0 pt-1">
                  <span className="block border-b-2 border-slate-200 pb-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:border-white/10 dark:text-slate-500">
                    Time away{filtered ? ' · all sources' : ''}
                  </span>
                </th>
              </tr>
              <tr className="border-b border-slate-100 dark:border-white/10">
                {/* The Rep column stays put while the rest scrolls on a narrow
                    screen (Nic 2026-11-02): sticky, on the card's own surface. */}
                <Th align="center" className={cn('sticky left-0 z-10 pl-5', SURFACE)}>Rep</Th>
                <Th align="center" className="px-2" info="New leads they got in this period, and how many they reached. Underneath: reached out within 2 minutes while available, then with nothing taken out.">Arrived · reached</Th>
                <Th align="center" className="px-2" info="Of the new leads they got in this period, how many are Dropped now. Re-engaging one takes it back off. Hover for the counts.">Dropped</Th>
                <th className="w-4 p-0" />
                <Th align="center" className="px-2" info="Every agreement they closed, with the share that came from leads and the share who made their own account first (direct signups, the rep still sends and gets the agreement signed). Hover for the median days from account to signed.">Signed</Th>
                <Th align="center" className="px-2" info="Live share of the clients they signed on a call. Hover for the counts.">Hot seats</Th>
                <Th align="center" className="px-2" info="Follow-ups booked, and how many didn't show.">Booked</Th>
                <th className="w-4 p-0" />
                <Th align="center" className="px-2" info="Breaks they turned on themselves: the total time, then how many breaks.">Breaks</Th>
                <Th align="center" info="Their meetings, from 5 minutes before to 5 minutes after." className="pl-2 pr-5">Meetings</Th>
              </tr>
            </thead>
            <tbody>
              {reps.length ? (
                reps.map((r) => {
                  const leadShare = r.signed ? Math.round(((r.leadSigned ?? r.signed - r.directSigned) / r.signed) * 100) : 0;
                  const leadSigned = r.leadSigned ?? r.signed - (r.directSigned || 0);
                  return (
                    <tr key={r.userId} className="border-b border-slate-100 last:border-0 dark:border-white/5">
                      <td className={cn('sticky left-0 z-10 py-3 pl-5 pr-2 text-center', SURFACE)}>
                        {/* Initials only (Nic 2026-10-26); the name on hover. */}
                        <span className="inline-flex" title={r.name} aria-label={r.name}>
                          <Avatar name={r.name} color={colors[r.userId]} />
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {r.leads ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-end gap-2">
                              <span className="flex flex-col items-center leading-none">
                                <span className="text-base font-semibold tabular-nums text-slate-900 dark:text-white">{r.leads}</span>
                                <span className="mt-0.5 text-[10px] text-slate-400">leads</span>
                              </span>
                              <span className="pb-2.5 text-[11px] text-slate-300 dark:text-slate-600">&rarr;</span>
                              <span className="flex flex-col items-center leading-none">
                                <span className="text-base font-semibold tabular-nums text-slate-900 dark:text-white">{r.reached}</span>
                                <span className="mt-0.5 text-[10px] text-slate-400">reached</span>
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400">
                              <span title={`${r.speedAvailable?.within2 ?? 0} of ${r.leads} reached out within 2 minutes while available`}>
                                <GoalPill value={r.speedAvailable?.pct} target={targets.speedPct} />
                              </span>
                              <span className="whitespace-nowrap tabular-nums">within 2 min · overall {fmtPct(r.speed.pct)}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {r.leads ? (
                          <span
                            title={`${r.dropped} of ${r.leads} dropped`}
                            className={cn(
                              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
                              r.dropped
                                ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                            )}
                          >
                            {fmtPct(r.droppedPct)}
                          </span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="w-4 p-0" />
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {r.signed ? (
                          <div
                            className="flex flex-col items-center gap-1"
                            title={`${leadSigned} from leads, ${r.directSigned || 0} direct${r.medianDaysToSign != null ? ` · median ${fmtDays(r.medianDaysToSign)} from account to signed` : ''}`}
                          >
                            <span className="text-base font-semibold tabular-nums text-slate-900 dark:text-white">{r.signed}</span>
                            <span className="flex h-1.5 w-[72px] overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                              <span className="bg-violet-500" style={{ width: `${leadShare}%` }} />
                              <span className="bg-teal-500" style={{ width: `${100 - leadShare}%` }} />
                            </span>
                            <span className="whitespace-nowrap text-[10px] tabular-nums text-slate-400">
                              <span className="mr-1 inline-block h-1.5 w-1.5 rounded-sm bg-violet-500 align-baseline" />
                              {leadSigned} lead{leadSigned === 1 ? '' : 's'}
                              {r.directSigned ? (
                                <>
                                  <span className="ml-1.5 mr-1 inline-block h-1.5 w-1.5 rounded-sm bg-teal-500 align-baseline" />
                                  {r.directSigned} direct
                                </>
                              ) : null}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center">
                        {/* The share alone, the counts on hover (Nic 2026-11-02): a
                            year of numbers must stay narrow, and a rep with
                            signings on a call and no transfer reads 0%, never a dash. */}
                        {r.hotSeat.onCall ? (
                          <span title={`${r.hotSeat.live} live of ${r.hotSeat.onCall} signed on a call${r.hotSeat.callback ? `, ${r.hotSeat.callback} callback` : ''}`}>
                            <GoalPill value={r.hotSeat.livePct} target={targets.hotSeatPct}>
                              {fmtPct(r.hotSeat.livePct)}
                            </GoalPill>
                          </span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums text-slate-700 dark:text-slate-200">
                        {r.meetings.booked}
                        {r.meetings.noShows ? (
                          <span className="ml-1 text-[11px] text-rose-500">{r.meetings.noShows} no-show</span>
                        ) : null}
                      </td>
                      <td className="w-4 p-0" />
                      <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums text-slate-700 dark:text-slate-200">
                        {r.away?.selfCount ? (
                          <>
                            {fmtMinutes(r.away.selfMinutes)}
                            <span className="ml-1 text-[11px] text-slate-400">({r.away.selfCount}×)</span>
                          </>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pl-2 pr-5 text-center tabular-nums text-slate-700 dark:text-slate-200">
                        {r.away?.meetingMinutes ? fmtMinutes(r.away.meetingMinutes) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-xs text-slate-400">
                    No Sales Reps yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BonusLadder bonus={data.bonus} unit="agreements" colors={colors} allSources={filtered} />
    </div>
  );
};

export default SalesTeamTab;
